import { invalidateAll as invalidateCalendarCache } from '@/composables/useCalendarStore'

// Local data an operator's session leaves behind in the browser, and the
// one place that clears it.
//
// Logging out ends the SERVER session — it does not empty localStorage,
// and a hard `window.location` redirect doesn't either. These stores
// outlive the session that wrote them:
//
//   - `stemne:cal:cache:<slug>` holds whole rendered calendar views. That
//     is not just layout: external Google/Outlook events keep their
//     titles, and Stemne bookings keep admin-scope customer contact
//     fields (`customer_email`, `customer_phone`). On a shared machine
//     the next person to open the browser can read the previous
//     operator's calendar and their customers' contact details straight
//     out of devtools.
//   - `stemne.search.recent` holds the operator's last search queries,
//     which in this app are overwhelmingly customer names.
//   - `stemne_onboarding` holds the in-progress operator profile and
//     workspace handoff token in sessionStorage.
//
// Deliberately NOT cleared: UI preferences (`stemne:nav:collapsed:*`,
// `stemne:tab:*`, `stemne.locale`, `stemne.calendar.hourPx`, dismissed
// banners). They carry no personal data, and wiping them makes the next
// login worse for no security gain.
//
// The codebase already treats provider event content as something to
// scrub the moment access ends — see `purgeExternalEvents()`, called on
// calendar disconnect for exactly this reason. Logout simply never got
// wired to the same standard.

const RECENT_SEARCH_KEY = 'stemne.search.recent'
const ONBOARDING_KEY = 'stemne_onboarding'

// clearOperatorLocalData wipes every browser-side cache that holds
// operator or customer data. Call it on EVERY path that ends the
// session, immediately before the redirect — there are several, and
// `src/__tests__/logoutTeardown.spec.ts` fails the build on a logout
// that forgets.
//
// Best-effort by construction: a blocked or full localStorage must never
// leave the operator stuck on a page they are trying to leave, so this
// never throws.
export function clearOperatorLocalData(): void {
  try {
    invalidateCalendarCache()
  } catch {
    // Never block a logout on cache teardown.
  }
  try {
    localStorage.removeItem(RECENT_SEARCH_KEY)
  } catch {
    // Private mode / blocked storage — nothing to clear.
  }
  try {
    sessionStorage.removeItem(ONBOARDING_KEY)
  } catch {
    // Private mode / blocked storage — nothing to clear.
  }
}
